'use client';

import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { TabId } from '../types/tabs';
import { cn } from '../utils/cn';
import { useNavigation } from '../contexts/NavigationContext';
import { useScrollManager } from '../contexts/ScrollManagerContext';
import { TabBar } from '../components/TabBar';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useRuntime } from '../runtime/RuntimeContext';
import { stripLangPrefix } from '../i18n/locale';

const TAB_ROOT_PATHNAMES: ReadonlySet<string> = new Set([
  '/',
  '/home',
  '/cases',
  '/inventory',
  '/profile',
  '/games',
  '/referrals',
  '/shop',
  '/payment',
]);

function isTabRootPathname(pathname: string | null): boolean {
  if (!pathname) return false;
  if (TAB_ROOT_PATHNAMES.has(pathname)) return true;
  if (pathname.startsWith('/shop/')) return true;
  return false;
}

type FixedMotion = 'none' | 'in' | 'out';

// WHY: must match `animate-shot-fixed-bottom-out` in animations.css
const FIXED_BOTTOM_EXIT_MS = 200;

const SCREEN_SETTLE_MS = 100;

const SCREEN_BOTTOM_GAP_PX = 16;

interface FixedElementState {
  content: ReactNode | null;
  title?: string;
  className?: string;
  height: number;
  showBlur: boolean;
  motion: FixedMotion;
}

interface TabBarState {
  visible: boolean;
  height: number;
}

export interface ScreenLayoutContextValue {
  fixedTop: FixedElementState;
  fixedBottom: FixedElementState;
  tabBar: TabBarState;
  disableSafeArea: boolean;
  isPreview: boolean;
  layoutReady: boolean;
  setFixedTop: (content: ReactNode, title?: string, className?: string) => void;
  setFixedBottom: (content: ReactNode, className?: string, showBlur?: boolean, animate?: boolean) => void;
  clearFixedTop: () => void;
  clearFixedBottom: (animate?: boolean) => void;
  setTabBarVisible: (visible: boolean) => void;
  clearTabBar: () => void;
  registerTabBarOverride: () => void;
  unregisterTabBarOverride: () => void;
  setDisableSafeArea: (disabled: boolean) => void;
  registerFixedTopRef: (element: HTMLElement | null) => void;
  registerFixedBottomRef: (element: HTMLElement | null) => void;
  registerTabBarRef: (element: HTMLElement | null) => void;
}

const ScreenLayoutContext = createContext<ScreenLayoutContextValue | null>(null);

interface ScreenLayoutProviderProps {
  children: ReactNode;
  className?: string;
  isPreview?: boolean;
}

const ScreenLayoutRenderer: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
  const context = useContext(ScreenLayoutContext);
  if (!context) throw new Error('ScreenLayoutRenderer must be used within ScreenLayoutProvider');

  const { fixedTop, fixedBottom, tabBar, registerFixedTopRef, registerFixedBottomRef, registerTabBarRef } = context;
  const navigation = useNavigation();
  const { clearAll: clearAllScroll } = useScrollManager();
  const isDesktop = useIsDesktop();

  const handleTabChange = useCallback((tabId: TabId) => {
    clearAllScroll();
    navigation.changeTab(tabId);
  }, [navigation, clearAllScroll]);

  const handleActiveTabClick = useCallback((tabId: TabId) => {
    clearAllScroll();
    navigation.resetStack(tabId, tabId);
  }, [navigation, clearAllScroll]);

  const layoutStyle = {
    '--screen-fixed-top-height': `${fixedTop.height}px`,
    '--screen-fixed-bottom-height': `${fixedBottom.height}px`,
    '--screen-tabbar-height': `${tabBar.visible ? tabBar.height : 0}px`,
    '--screen-fixed-bottom-lift': fixedBottom.content
      ? 'max(calc(var(--screen-tabbar-height,0px) + min(8px, var(--screen-tabbar-height,0px))), calc(var(--tg-viewport-content-safe-area-inset-bottom,0px) + var(--tg-viewport-safe-area-inset-bottom,0px)))'
      : '0px',
    '--screen-fixed-bottom-gap': fixedBottom.content ? `${SCREEN_BOTTOM_GAP_PX}px` : '0px',
  };

  return (
    <div className={cn('absolute top-0 start-0 end-0 bottom-0', className)} style={layoutStyle}>

      {(fixedTop.content || fixedTop.title) && !isDesktop && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-40"
          style={{
            height: 'calc(var(--screen-fixed-top-height,0px) + 24px)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            maskImage: 'linear-gradient(to bottom, #000 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000 40%, transparent 100%)',
          }}
        />
      )}

      {(fixedTop.content || (fixedTop.title && !isDesktop)) && (
        <div
          ref={registerFixedTopRef}
          className={cn(
            'absolute top-0 start-0 end-0 z-50 min-h-[calc(var(--tg-viewport-content-safe-area-inset-top,0px)+var(--tg-viewport-safe-area-inset-top,0px))]',
            isDesktop && 'bg-transparent shadow-none',
            fixedTop.className
          )}
        >
          {fixedTop.title && !isDesktop && (
            <div className="mt-[var(--tg-viewport-safe-area-inset-top,0px)] h-[var(--tg-viewport-content-safe-area-inset-top,46px)] text-lg font-semibold leading-snug text-center m-0 flex items-center justify-center box-border">
              {fixedTop.title}
            </div>
          )}
          {fixedTop.content}
        </div>
      )}

      {children}


      {fixedBottom.content && fixedBottom.showBlur && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 z-10',
            fixedBottom.motion === 'in' && 'animate-shot-fixed-bottom-scrim-in',
            fixedBottom.motion === 'out' && 'animate-shot-fixed-bottom-scrim-out'
          )}
          style={{
            height: 'calc(var(--screen-fixed-bottom-height,0px) + var(--screen-fixed-bottom-lift,0px) + 48px)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            maskImage: 'linear-gradient(to top, #000 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to top, #000 40%, transparent 100%)',
          }}
        />
      )}

      {fixedBottom.content && (
        <div
          ref={registerFixedBottomRef}
          className={cn(
            'absolute bottom-[var(--screen-fixed-bottom-lift,0px)] start-0 end-0 z-40',
            fixedBottom.motion === 'in' && 'animate-shot-fixed-bottom-in',
            fixedBottom.motion === 'out' && 'animate-shot-fixed-bottom-out pointer-events-none',
            fixedBottom.className
          )}
        >
          {fixedBottom.content}
        </div>
      )}

      {tabBar.visible && !isDesktop && (
        <div ref={registerTabBarRef} className="absolute bottom-0 start-0 end-0 z-[11]">
          <TabBar
            activeTab={navigation.state.activeTab}
            onTabChange={handleTabChange}
            onActiveTabClick={handleActiveTabClick}
          />
        </div>
      )}
    </div>
  );
};

export const ScreenLayoutProvider: FC<ScreenLayoutProviderProps> = ({ children, className, isPreview = false }) => {
  const [fixedTop, setFixedTopState] = useState<FixedElementState>({
    content: null,
    className: undefined,
    height: 0,
    showBlur: true,
    motion: 'none',
  });

  const [fixedBottom, setFixedBottomState] = useState<FixedElementState>({
    content: null,
    className: undefined,
    height: 0,
    showBlur: true,
    motion: 'none',
  });

  const screenChangedAtRef = useRef(0);
  const screenChildrenRef = useRef<ReactNode>(undefined);
  if (screenChildrenRef.current !== children) {
    screenChildrenRef.current = children;
    screenChangedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
  }
  const exitTimerRef = useRef<number | null>(null);
  const isNavigating = useCallback(() => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return now - screenChangedAtRef.current < SCREEN_SETTLE_MS;
  }, []);

  const pathname = usePathname();
  const runtime = useRuntime();
  const [tabBarHeight, setTabBarHeight] = useState(0);
  const [tabBarOverrideCount, setTabBarOverrideCount] = useState(0);
  const [tabBarManualVisible, setTabBarManualVisible] = useState<boolean | null>(null);

  // WHY: TMA keeps a static `/` URL across stack navigation — pathname detection would show the tab bar on every screen
  const tabBarVisible =
    tabBarManualVisible !== null
      ? tabBarManualVisible
      : tabBarOverrideCount > 0 || (runtime === 'web' && isTabRootPathname(stripLangPrefix(pathname)));
  const tabBar: TabBarState = { visible: tabBarVisible, height: tabBarHeight };

  const [disableSafeArea, setDisableSafeArea] = useState(false);
  const [layoutReady, setLayoutReady] = useState(false);

  const fixedTopRef = useRef<HTMLElement | null>(null);
  const fixedBottomRef = useRef<HTMLElement | null>(null);
  const tabBarRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const rafIdRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (isPreview) {
      if (fixedTopRef.current) {
        const height = fixedTopRef.current.offsetHeight;
        if (height > 0) {
          setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
        }
      }
      if (fixedBottomRef.current) {
        const height = fixedBottomRef.current.offsetHeight;
        if (height > 0) {
          setFixedBottomState((prev) => (prev.height !== height ? { ...prev, height } : prev));
        }
      }
      if (tabBarRef.current) {
        const height = tabBarRef.current.offsetHeight;
        if (height > 0) {
          setTabBarHeight((prev) => (prev !== height ? height : prev));
        }
      }
      setLayoutReady(true);
      return;
    }

    const observeElement = (element: HTMLElement | null, type: 'top' | 'bottom') => {
      if (!element) return;
      const height = element.offsetHeight;
      if (type === 'top') {
        setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
      } else {
        setFixedBottomState((prev) => (prev.height !== height ? { ...prev, height } : prev));
      }
      resizeObserverRef.current?.observe(element);
    };

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === fixedTopRef.current) {
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
          }
          rafIdRef.current = requestAnimationFrame(() => {
            const height = entry.target.getBoundingClientRect().height;
            setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
          });
        } else if (entry.target === fixedBottomRef.current) {
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
          }
          rafIdRef.current = requestAnimationFrame(() => {
            const height = entry.target.getBoundingClientRect().height;
            setFixedBottomState((prev) => (prev.height !== height ? { ...prev, height } : prev));
          });
        } else if (entry.target === tabBarRef.current) {
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
          }
          rafIdRef.current = requestAnimationFrame(() => {
            const height = entry.target.getBoundingClientRect().height;
            setTabBarHeight((prev) => (prev !== height ? height : prev));
          });
        }
      }
    });

    if (fixedTopRef.current) {
      const height = fixedTopRef.current.offsetHeight;
      setFixedTopState((prev) => (prev.height !== height ? { ...prev, height } : prev));
      observeElement(fixedTopRef.current, 'top');
    }
    if (fixedBottomRef.current) {
      observeElement(fixedBottomRef.current, 'bottom');
    }
    if (tabBarRef.current) {
      const height = tabBarRef.current.offsetHeight;
      setTabBarHeight((prev) => (prev !== height ? height : prev));
      resizeObserverRef.current.observe(tabBarRef.current);
    }

    setLayoutReady(true);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      resizeObserverRef.current?.disconnect();
    };
  }, [isPreview, fixedTop.content, fixedTop.title, fixedBottom.content, tabBarVisible]);

  useLayoutEffect(() => {
    if (isPreview) return;
    if (tabBarVisible && tabBarHeight === 0 && tabBarRef.current) {
      const height = tabBarRef.current.offsetHeight;
      if (height > 0) {
        setTabBarHeight(height);
      }
    }
  }, [isPreview, tabBarVisible, tabBarHeight]);

  useLayoutEffect(() => {
    if (isPreview) return;
    if ((fixedTop.content || fixedTop.title) && fixedTop.height === 0 && fixedTopRef.current) {
      const height = fixedTopRef.current.offsetHeight;
      if (height > 0) {
        setFixedTopState((prev) => ({ ...prev, height }));
      }
    }
  }, [isPreview, fixedTop.content, fixedTop.title, fixedTop.height]);

  const setFixedTop = useCallback((content: ReactNode, title?: string, className?: string) => {
    setFixedTopState((prev) => ({ content, title, className, height: prev.height, showBlur: true, motion: 'none' }));
  }, []);

  const setFixedBottom = useCallback((
    content: ReactNode,
    className?: string,
    showBlur = true,
    animate = false
  ) => {
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    const withMotion = animate && !isPreview && !isNavigating();
    setFixedBottomState((prev) => {
      const appearing = prev.content == null || prev.motion === 'out';
      const motion: FixedMotion = appearing ? (withMotion ? 'in' : 'none') : prev.motion;
      return { content, className, height: prev.height, showBlur, motion };
    });
  }, [isPreview, isNavigating]);

  const clearFixedTop = useCallback(() => {
    setFixedTopState({ content: null, title: undefined, className: undefined, height: 0, showBlur: true, motion: 'none' });
  }, []);

  const dropFixedBottom = useCallback(() => {
    setFixedBottomState({ content: null, className: undefined, height: 0, showBlur: true, motion: 'none' });
  }, []);

  const clearFixedBottom = useCallback((animate = false) => {
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (!animate || isPreview || isNavigating()) {
      dropFixedBottom();
      return;
    }
    setFixedBottomState((prev) => (prev.content == null ? prev : { ...prev, motion: 'out' }));
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null;
      dropFixedBottom();
    }, FIXED_BOTTOM_EXIT_MS);
  }, [dropFixedBottom, isPreview, isNavigating]);

  useEffect(() => () => {
    if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
  }, []);

  const registerFixedTopRef = useCallback((element: HTMLElement | null) => {
    fixedTopRef.current = element;
  }, []);

  const registerFixedBottomRef = useCallback((element: HTMLElement | null) => {
    fixedBottomRef.current = element;
  }, []);

  const setTabBarVisible = useCallback((visible: boolean) => {
    setTabBarManualVisible(visible);
  }, []);

  const clearTabBar = useCallback(() => {
    setTabBarManualVisible(null);
    setTabBarHeight(0);
  }, []);

  const registerTabBarOverride = useCallback(() => {
    setTabBarOverrideCount((c) => c + 1);
  }, []);

  const unregisterTabBarOverride = useCallback(() => {
    setTabBarOverrideCount((c) => Math.max(0, c - 1));
  }, []);

  const registerTabBarRef = useCallback((element: HTMLElement | null) => {
    tabBarRef.current = element;
  }, []);

  const value: ScreenLayoutContextValue = {
    fixedTop,
    fixedBottom,
    tabBar,
    disableSafeArea,
    isPreview,
    layoutReady,
    setFixedTop,
    setFixedBottom,
    clearFixedTop,
    clearFixedBottom,
    setTabBarVisible,
    clearTabBar,
    registerTabBarOverride,
    unregisterTabBarOverride,
    setDisableSafeArea,
    registerFixedTopRef,
    registerFixedBottomRef,
    registerTabBarRef,
  };

  return (
    <ScreenLayoutContext.Provider value={value}>
      <ScreenLayoutRenderer className={className}>{children}</ScreenLayoutRenderer>
    </ScreenLayoutContext.Provider>
  );
};

export { ScreenLayoutContext };
